import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Auth } from './auth';
import { Callable, FN_REGION } from './callable';

/** Ce qu'une invitation dit d'elle-même à qui n'a pas encore de compte. */
export interface InvitePreview {
  eventName: string;
  eventType: string;
  managerName: string;
  memberCount: number;
}

/** Invitation retrouvée au premier lancement, après installation. */
export interface ClaimedInvite {
  eventId: string;
  groupId: string | null;
  token: string;
  eventName: string;
  managerName: string;
}

type ClaimResponse = ({ found: true } & ClaimedInvite) | { found: false };

/** Le strict nécessaire pour rouvrir l'écran d'invitation plus tard. */
export interface PendingInvite {
  eventId: string;
  groupId: string | null;
  token: string;
}

/**
 * Marque l'installation dont l'invitation a déjà été récupérée.
 *
 * Posé seulement quand une invitation a été TROUVÉE, jamais sur un lancement à
 * vide : le poser d'emblée condamnait une invitation reçue plus tard — ouvrir
 * l'application une fois avant de cliquer le lien suffisait à la perdre, sans
 * aucun moyen de la retrouver ensuite. Le parc déjà installé, lui, est écarté
 * par le filtre de session ci-dessous, qui rend ce garde-fou-là superflu.
 */
const CLAIM_ATTEMPTED_KEY = 'invitation.deferredClaimAttempted';

/**
 * Invitation reçue mais pas encore aboutie.
 *
 * Elle ne peut pas ne vivre que dans l'URL : l'inscription par téléphone oblige
 * à QUITTER l'application pour lire le SMS, et le système peut la suspendre ou
 * la tuer entre-temps. Au retour, la route est perdue — et avec elle
 * l'invitation, puisque le serveur supprime son dépôt à la réclamation et que
 * le drapeau ci-dessus interdit d'en redemander une. La retenir ici est ce qui
 * la fait survivre au parcours d'inscription.
 */
const PENDING_KEY = 'invitation.pending';

/**
 * Au-delà, on cesse de la reproposer : une invitation qu'on n'a pas acceptée en
 * un jour n'est plus celle qu'on attendait en installant l'application.
 */
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Le lien d'invitation, de la page web reçue sur WhatsApp jusqu'à l'application.
 *
 * Deux moments, deux appels : `open()` quand le lien est ouvert dans un
 * navigateur — il donne de quoi afficher une page reconnaissable et dépose
 * l'invitation côté serveur — puis `resumePending()` au premier lancement,
 * qui la récupère pour que personne n'ait à retrouver le message et à recliquer.
 */
@Injectable({ providedIn: 'root' })
export class Invitation {
  private callable = inject(Callable);
  private router = inject(Router);
  private auth = inject(Auth);

  /**
   * Aperçu de l'invitation pour la page de redirection.
   *
   * `platform` n'est transmis que depuis un mobile : c'est lui qui déclenche le
   * dépôt en vue de la reprise après installation, inutile ailleurs.
   */
  async open(data: {
    eventId: string;
    groupId?: string;
    token: string;
    platform?: 'ios' | 'android';
  }): Promise<InvitePreview> {
    return this.callable.call<typeof data, InvitePreview>(
      FN_REGION.W1,
      'openInvitation',
      data
    );
  }

  /**
   * Reprend l'invitation ouverte avant l'installation et ouvre la page d'accès à
   * l'événement. Rend `true` si une invitation a été retrouvée.
   *
   * Silencieuse par construction : n'avoir rien à reprendre est le cas courant —
   * la plupart des installations ne viennent pas d'un lien — et une panne réseau
   * au premier lancement ne doit pas retarder l'écran d'accueil.
   */
  async resumePending(): Promise<boolean> {
    // Appelée au démarrage ET à l'ouverture d'une session : le parcours
    // d'inscription peut avoir égaré la route de retour, auquel cas
    // l'invitation doit reparaître dès que la personne est connectée.
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios' && platform !== 'android') return false;
    // L'application ouverte PAR le lien n'a rien à reprendre : le deep-link a
    // déjà mené à l'invitation, et republier la même route l'écraserait.
    if (this.router.url.startsWith('/join')) return false;

    // Une invitation reçue mais pas encore acceptée revient d'abord — quel que
    // soit l'état de la session. C'est justement au retour du SMS, une fois
    // inscrit, qu'elle doit reparaître : sans cela le parcours d'inscription la
    // perdait, et rien ne permettait plus de la retrouver.
    const pending = await this.readPending();
    if (pending) return this.openJoinScreen(pending);

    // Sinon, rapprochement réseau. Réservé au premier lancement d'une
    // installation fraîche : une session déjà ouverte, c'est le parc existant
    // que la mise à jour atteint aussi, et l'une de ces applications — sortant
    // par la même adresse qu'un vrai nouveau venu — capterait son invitation.
    await this.auth.authReady;
    if (this.auth.currentUser()) return false;

    try {
      const { value } = await Preferences.get({ key: CLAIM_ATTEMPTED_KEY });
      if (value === '1') return false;

      const result = await this.callable.call<{ platform: string }, ClaimResponse>(
        FN_REGION.W1,
        'claimInvitation',
        { platform }
      );
      if (!result?.found) return false;

      // Marqué APRÈS coup : rien n'a été consommé tant que rien n'a été trouvé.
      await Preferences.set({ key: CLAIM_ATTEMPTED_KEY, value: '1' });
      await this.remember(result);
      return this.openJoinScreen(result);
    } catch {
      return false;
    }
  }

  /**
   * Retient une invitation jusqu'à ce qu'elle aboutisse.
   *
   * Appelée aussi bien pour une invitation retrouvée après installation que
   * pour un lien ouvert dans l'application : les deux mènent au même parcours
   * d'inscription, donc à la même sortie vers les SMS.
   */
  async remember(invite: PendingInvite): Promise<void> {
    try {
      await Preferences.set({
        key: PENDING_KEY,
        value: JSON.stringify({ ...invite, expiresAt: Date.now() + PENDING_TTL_MS }),
      });
    } catch {
      // Stockage indisponible : l'invitation ne survivra pas à une relance, mais
      // le parcours en cours reste intact. Rien à signaler à l'usager.
    }
  }

  /** L'invitation a abouti — ou ne concernait pas cette personne. */
  async forget(): Promise<void> {
    try {
      await Preferences.remove({ key: PENDING_KEY });
    } catch {
      // Sans conséquence : une invitation périmée cesse d'être reproposée.
    }
  }

  /** Invitation retenue encore valable, sinon `null` (et le reliquat est effacé). */
  private async readPending(): Promise<PendingInvite | null> {
    try {
      const { value } = await Preferences.get({ key: PENDING_KEY });
      if (!value) return null;
      const saved = JSON.parse(value) as PendingInvite & { expiresAt?: number };
      if (!saved?.eventId || !saved?.token) return null;
      if (!saved.expiresAt || saved.expiresAt < Date.now()) {
        await this.forget();
        return null;
      }
      return { eventId: saved.eventId, groupId: saved.groupId ?? null, token: saved.token };
    } catch {
      return null;
    }
  }

  /**
   * Ouvre l'écran d'accès à l'événement.
   *
   * `via=install` : la page sait qu'elle affiche une invitation retrouvée par
   * rapprochement réseau, et non un lien ouvert délibérément — elle offre donc
   * de la refuser, seul garde-fou contre un rapprochement qui viserait à côté.
   */
  private async openJoinScreen(invite: PendingInvite): Promise<boolean> {
    await this.router.navigate(['/join'], {
      queryParams: {
        eventId: invite.eventId,
        groupId: invite.groupId ?? undefined,
        token: invite.token,
        via: 'install',
      },
    });
    return true;
  }
}
